import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Bot, Mic, MicOff, Send, Loader2, Swords, Flag,
  Volume2, VolumeX, Sparkles, AlertCircle, Type, Square, Play, Pause
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL as API } from '../config/api';

export default function BattleBotPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(false);
  const [verse, setVerse] = useState('');
  const [messages, setMessages] = useState([]);
  const [rateLimit, setRateLimit] = useState({ remaining: 10 });

  // Settings
  const [barCount, setBarCount] = useState(8);
  const [cleanMode, setCleanMode] = useState(true);
  const [battleMode, setBattleMode] = useState('text'); // 'text' or 'voice'

  // Voice mode state
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [transcribing, setTranscribing] = useState(false);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    checkRateLimit();
    fetchVoices();
  }, [isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioPlayerRef.current) { audioPlayerRef.current.pause(); audioPlayerRef.current = null; }
    };
  }, []);

  const checkRateLimit = async () => {
    try {
      const res = await fetch(`${API}/api/battle-bot/rate-limit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRateLimit(await res.json());
    } catch {}
  };

  const fetchVoices = async () => {
    try {
      const res = await fetch(`${API}/api/battlebot/voices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const all = [...(data.elevenlabs_voices || []), ...(data.openai_voices || [])];
        setVoices(all);
        setSelectedVoice(data.default_voice || all[0]?.id || '');
      }
    } catch {}
  };

  const startSession = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/battlebot/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ battle_mode: 'clever_assassin', bar_count: barCount, clean_mode: cleanMode })
      });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setMessages([]);
        toast.success(`${battleMode === 'voice' ? 'Voice' : 'Text'} battle started!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start');
      }
    } catch { toast.error('Network error'); }
    finally { setLoading(false); }
  };

  // ============ TEXT MODE ============
  const submitVerse = async () => {
    if (!verse.trim() || !session) return;
    if (rateLimit.remaining <= 0) { toast.error('Rate limit reached'); return; }

    setResponding(true);
    const userMsg = { speaker: 'user', text: verse.trim(), mode: 'text', timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setVerse('');

    try {
      const res = await fetch(`${API}/api/battlebot/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ session_id: session.session_id, user_verse: verse.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        const botMsg = {
          speaker: 'bot', text: data.bot_verse || data.response, mode: 'text',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, botMsg]);
        setRateLimit(prev => ({ ...prev, remaining: Math.max(0, prev.remaining - 1) }));
      } else toast.error('Bot failed to respond');
    } catch { toast.error('Network error'); }
    finally { setResponding(false); }
  };

  // ============ VOICE MODE ============
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
      };

      mediaRecorder.start(250);
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;

    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        resolve();
      };
      mediaRecorderRef.current.stop();
      setRecording(false);
    });
  };

  const handleVoiceSubmit = async () => {
    await stopRecording();
    if (audioChunksRef.current.length === 0) { toast.error('No audio recorded'); return; }
    if (rateLimit.remaining <= 0) { toast.error('Rate limit reached'); return; }

    setTranscribing(true);
    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

    // Add "recording" message
    const userMsg = { speaker: 'user', text: 'Transcribing...', mode: 'voice', timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      // Step 1: STT
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      const sttRes = await fetch(`${API}/api/battlebot/stt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!sttRes.ok) { toast.error('Transcription failed'); setTranscribing(false); return; }
      const sttData = await sttRes.json();
      const transcript = sttData.text;

      if (!transcript || transcript.trim().length === 0) {
        toast.error('Could not hear you. Try again.');
        setMessages(prev => prev.slice(0, -1));
        setTranscribing(false);
        return;
      }

      // Update user message with transcript
      setMessages(prev => prev.map((m, i) =>
        i === prev.length - 1 ? { ...m, text: transcript } : m
      ));
      setTranscribing(false);
      setResponding(true);

      // Step 2: Get bot text response
      const respondRes = await fetch(`${API}/api/battlebot/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ session_id: session.session_id, user_verse: transcript })
      });

      if (!respondRes.ok) { toast.error('Bot failed to respond'); setResponding(false); return; }
      const botData = await respondRes.json();
      const botText = botData.bot_verse || botData.response;

      // Step 3: TTS — convert bot's response to audio
      const ttsRes = await fetch(`${API}/api/battlebot/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: botText, voice_id: selectedVoice, session_id: session.session_id })
      });

      let audioUrl = null;
      if (ttsRes.ok) {
        const ttsData = await ttsRes.json();
        audioUrl = ttsData.audio_url;
      }

      const botMsg = {
        speaker: 'bot', text: botText, mode: 'voice', audioUrl,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, botMsg]);
      setRateLimit(prev => ({ ...prev, remaining: Math.max(0, prev.remaining - 1) }));

      // Auto-play bot response
      if (audioUrl) playAudio(audioUrl);

    } catch (e) {
      toast.error('Voice battle error');
      console.error(e);
    } finally {
      setTranscribing(false);
      setResponding(false);
    }
  };

  const playAudio = (url) => {
    if (audioPlayerRef.current) { audioPlayerRef.current.pause(); }
    const audio = new Audio(url);
    audioPlayerRef.current = audio;
    setPlayingAudio(url);
    audio.onended = () => setPlayingAudio(null);
    audio.onerror = () => { setPlayingAudio(null); toast.error('Audio playback failed'); };
    audio.play().catch(() => setPlayingAudio(null));
  };

  const stopAudio = () => {
    if (audioPlayerRef.current) { audioPlayerRef.current.pause(); audioPlayerRef.current = null; }
    setPlayingAudio(null);
  };

  const endSession = async () => {
    if (!session) return;
    try {
      await fetch(`${API}/api/battlebot/session/${session.session_id}/reset`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch {}
    setSession(null);
    setMessages([]);
    stopAudio();
    toast.success('Battle ended!');
  };

  const reportResponse = async (messageIndex) => {
    toast.info('Report noted. Thank you for keeping battles respectful.');
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pb-32 px-4 pt-6" data-testid="battle-bot-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/20">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-foreground">Battle Bot</h1>
              <p className="text-sm text-muted-foreground">AI Opponent for Cypher Battles</p>
            </div>
          </div>
        </div>

        {/* Rate Limit Warning */}
        {rateLimit.remaining <= 3 && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-yellow-400">{rateLimit.remaining} responses remaining this hour</span>
          </div>
        )}

        {!session ? (
          /* ============ SESSION SETUP ============ */
          <Card className="p-5 bg-muted/20 border-border" data-testid="battle-setup">
            <h2 className="text-lg font-bold mb-5 flex items-center gap-2 text-white">
              <Swords className="w-5 h-5 text-orange-400" />Start a Battle
            </h2>

            <div className="space-y-5">
              {/* Battle Mode Toggle */}
              <div>
                <Label className="text-sm font-medium mb-2.5 block text-foreground/80">Battle Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setBattleMode('text')}
                    data-testid="mode-text"
                    className={`p-3 rounded-xl flex flex-col items-center gap-2 border transition-all ${
                      battleMode === 'text'
                        ? 'bg-orange-500/15 border-orange-500/40 text-orange-400'
                        : 'bg-muted/20 border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Type className="w-6 h-6" />
                    <div className="text-center">
                      <p className="font-semibold text-sm">Text Battle</p>
                      <p className="text-[10px] mt-0.5 opacity-70">Type your bars</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setBattleMode('voice')}
                    data-testid="mode-voice"
                    className={`p-3 rounded-xl flex flex-col items-center gap-2 border transition-all ${
                      battleMode === 'voice'
                        ? 'bg-red-500/15 border-red-500/40 text-red-400'
                        : 'bg-muted/20 border-border text-muted-foreground hover:border-border'
                    }`}
                  >
                    <Mic className="w-6 h-6" />
                    <div className="text-center">
                      <p className="font-semibold text-sm">Voice Battle</p>
                      <p className="text-[10px] mt-0.5 opacity-70">Rap into the mic</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Bar Count */}
              <div>
                <Label className="text-sm font-medium mb-2.5 block text-foreground/80">Bar Count</Label>
                <div className="flex gap-2">
                  {[4, 8, 16].map(count => (
                    <Button key={count}
                      variant={barCount === count ? 'default' : 'outline'}
                      onClick={() => setBarCount(count)}
                      className={`flex-1 ${barCount === count ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                    >
                      {count} Bars
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {battleMode === 'voice' ? 'Rap this many bars into the mic' : 'Write this many lines per round'}
                </p>
              </div>

              {/* Clean Mode */}
              <div className="flex items-center justify-between p-3 bg-muted/20 border border-border rounded-lg">
                <div>
                  <Label className="font-medium text-foreground/90">Clean Mode</Label>
                  <p className="text-xs text-muted-foreground">No profanity or explicit content</p>
                </div>
                <Switch checked={cleanMode} onCheckedChange={setCleanMode} />
              </div>

              {/* Voice Selection (Voice mode only) */}
              {battleMode === 'voice' && voices.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block text-foreground/80">Bot Voice</Label>
                  <select
                    value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)}
                    data-testid="voice-select"
                    className="w-full rounded-md bg-muted/50 border border-border text-sm text-foreground px-3 py-2"
                  >
                    {voices.map(v => (
                      <option key={v.id} value={v.id}>{v.name} — {v.description}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Rules */}
              <div className="p-3 bg-muted/20 border border-border rounded-lg">
                <h3 className="font-medium mb-2 text-sm flex items-center gap-2 text-foreground/80">
                  <Sparkles className="w-4 h-4 text-orange-400" />Battle Rules
                </h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {battleMode === 'voice' ? (
                    <>
                      <li>Tap the mic, spit your bars, tap again to submit</li>
                      <li>Bot listens, then fires back with voice</li>
                      <li>Bot will reference your lines (callbacks)</li>
                      <li>10 responses per hour limit</li>
                    </>
                  ) : (
                    <>
                      <li>Write your verse, Bot responds with matching bars</li>
                      <li>Bot will reference your lines (callbacks)</li>
                      <li>Keep it competitive but respectful</li>
                      <li>10 responses per hour limit</li>
                    </>
                  )}
                </ul>
              </div>

              {/* Start */}
              <Button onClick={startSession} disabled={loading}
                className="w-full h-12 text-base bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                data-testid="start-battle-btn">
                {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Swords className="w-5 h-5 mr-2" />}
                Start {battleMode === 'voice' ? 'Voice' : 'Text'} Battle
              </Button>
            </div>
          </Card>
        ) : (
          /* ============ BATTLE ARENA ============ */
          <div className="space-y-4" data-testid="battle-arena">
            {/* Session bar */}
            <div className="flex items-center justify-between p-3 bg-muted/20 border border-border rounded-lg">
              <div className="flex items-center gap-2">
                <Badge className={`${battleMode === 'voice' ? 'bg-red-500/20 text-red-400 border-red-500/20' : 'bg-orange-500/20 text-orange-400 border-orange-500/20'}`}>
                  {battleMode === 'voice' ? 'Voice' : 'Text'} | {barCount} Bars
                </Badge>
                {cleanMode && <Badge className="bg-green-500/20 text-green-400 border-green-500/20">Clean</Badge>}
                <span className="text-xs text-muted-foreground">Round {Math.ceil(messages.length / 2) || 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{rateLimit.remaining} left</span>
                <Button variant="ghost" size="sm" onClick={endSession} className="text-muted-foreground hover:text-foreground text-xs"
                  data-testid="end-battle-btn">
                  End Battle
                </Button>
              </div>
            </div>

            {/* Messages */}
            <Card className="p-4 bg-muted/20 border-border min-h-[350px] max-h-[450px] overflow-y-auto"
              data-testid="battle-messages">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center py-16">
                  <div>
                    <Bot className="w-12 h-12 mx-auto mb-3 text-muted-foreground/60" />
                    <p className="text-muted-foreground">{battleMode === 'voice' ? 'Hit record and drop your bars!' : 'Drop your first verse to begin!'}</p>
                    <p className="text-sm text-muted-foreground/80 mt-1">Battle Bot is ready to respond.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence>
                    {messages.map((msg, index) => (
                      <motion.div key={index} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.speaker === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-xl p-3.5 ${
                          msg.speaker === 'user'
                            ? 'bg-orange-500/10 border border-orange-500/20'
                            : 'bg-muted/50 border border-border'
                        }`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            {msg.speaker === 'bot'
                              ? <Bot className="w-3.5 h-3.5 text-red-400" />
                              : msg.mode === 'voice' ? <Mic className="w-3.5 h-3.5 text-orange-400" /> : <Type className="w-3.5 h-3.5 text-orange-400" />}
                            <span className="text-xs font-semibold text-foreground/80">
                              {msg.speaker === 'bot' ? 'Battle Bot' : 'You'}
                            </span>
                            {msg.mode === 'voice' && (
                              <Badge className="bg-muted/50 text-muted-foreground text-[9px] h-4 px-1.5">VOICE</Badge>
                            )}
                          </div>

                          <div className="font-mono text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">
                            {msg.text}
                          </div>

                          {/* Audio playback for voice mode bot responses */}
                          {msg.speaker === 'bot' && msg.audioUrl && (
                            <button
                              onClick={() => playingAudio === msg.audioUrl ? stopAudio() : playAudio(msg.audioUrl)}
                              className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/25 text-red-400 text-xs hover:bg-red-500/25 transition-all"
                              data-testid={`play-response-${index}`}
                            >
                              {playingAudio === msg.audioUrl
                                ? <><Pause className="w-3 h-3" /> Playing...</>
                                : <><Volume2 className="w-3 h-3" /> Play Response</>}
                            </button>
                          )}

                          {msg.speaker === 'bot' && (
                            <button onClick={() => reportResponse(index)}
                              className="mt-2 text-[10px] text-muted-foreground/80 hover:text-red-400 flex items-center gap-1">
                              <Flag className="w-3 h-3" />Report
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {(responding || transcribing) && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                      <div className="bg-muted/50 border border-border rounded-xl p-3.5">
                        <div className="flex items-center gap-2">
                          <Bot className="w-3.5 h-3.5 text-red-400" />
                          <span className="text-xs font-semibold text-foreground/80">Battle Bot</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-xs">{transcribing ? 'Listening...' : 'Writing bars...'}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </Card>

            {/* ============ INPUT AREA ============ */}
            {battleMode === 'text' ? (
              /* Text Input */
              <Card className="p-4 bg-muted/20 border-border" data-testid="text-input-area">
                <Textarea value={verse} onChange={e => setVerse(e.target.value)}
                  placeholder={`Drop your ${barCount} bars here...`}
                  className="min-h-[100px] font-mono text-sm resize-none bg-muted/30 border-border mb-3"
                  disabled={responding} data-testid="verse-input" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {verse.split('\n').filter(l => l.trim()).length} / {barCount} lines
                  </span>
                  <Button onClick={submitVerse}
                    disabled={!verse.trim() || responding || rateLimit.remaining <= 0}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="submit-verse-btn">
                    {responding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Drop Bars
                  </Button>
                </div>
              </Card>
            ) : (
              /* Voice Input */
              <Card className="p-5 bg-muted/20 border-border" data-testid="voice-input-area">
                <div className="flex flex-col items-center gap-4">
                  {recording ? (
                    <>
                      {/* Recording indicator */}
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center animate-pulse">
                          <div className="w-14 h-14 rounded-full bg-red-500/30 flex items-center justify-center">
                            <Mic className="w-7 h-7 text-red-400" />
                          </div>
                        </div>
                        <div className="absolute -top-1 -right-1 px-2 py-0.5 bg-red-500 rounded-full">
                          <span className="text-[10px] font-bold text-foreground">{recordingTime}s</span>
                        </div>
                      </div>
                      <p className="text-sm text-red-400 font-medium">Recording... Spit your bars!</p>
                      <Button onClick={handleVoiceSubmit}
                        className="bg-red-600 hover:bg-red-700 text-white px-8"
                        data-testid="stop-recording-btn">
                        <Square className="w-4 h-4 mr-2" />Stop & Submit
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={startRecording}
                        disabled={responding || transcribing || rateLimit.remaining <= 0}
                        className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100"
                        data-testid="record-btn"
                      >
                        <Mic className="w-8 h-8 text-white" />
                      </button>
                      <p className="text-sm text-muted-foreground">
                        {responding ? 'Bot is responding...' : transcribing ? 'Processing...' : 'Tap to record your bars'}
                      </p>
                    </>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Tips */}
        <Card className="mt-5 p-4 bg-muted/20 border-border">
          <h3 className="font-medium text-sm mb-2 text-foreground/80">
            {battleMode === 'voice' ? 'Voice Battle Tips' : 'Battle Tips'}
          </h3>
          <ul className="text-xs text-muted-foreground space-y-1">
            {battleMode === 'voice' ? (
              <>
                <li>Rap clearly into the mic — AI transcribes your bars</li>
                <li>Bot fires back with voice and text</li>
                <li>Use wordplay and callbacks for better responses</li>
                <li>Choose a bot voice that matches the vibe</li>
              </>
            ) : (
              <>
                <li>Use wordplay and metaphors for better responses</li>
                <li>The bot will reference your specific lines</li>
                <li>Keep it creative and competitive</li>
                <li>Report any inappropriate bot responses</li>
              </>
            )}
          </ul>
        </Card>
      </div>
    </div>
  );
}
